import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, filter, map, retry } from 'rxjs/operators';
import { EMPTY, of } from 'rxjs';
import {
  PaymentLinkLandingComponentPaymentLinkActions,
  PaymentLinkLandingComponentPermissionActions,
} from '../actions';
import { HttpErrorResponse } from '@angular/common/http';
import { selectGetSessionFromPaymentLinkResponse } from '../../../../store/reducers';
import { PermissionV1Service } from '@arc/arc-shared-frontend';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { concatLatestFrom } from '@ngrx/operators';
import { selectVerifyUserIsProvisionedRetryConfig } from '@arc/unified-payments-frontend/shared-stores';
import {
  TranslateActions,
  supportedLanguages,
} from '@arc/unified-payments-frontend/i18n';

@Injectable()
export class PaymentLinkLandingComponentPaymentLinkEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private router: Router,
    private permissionService: PermissionV1Service,
  ) {}

  verifyUserIsProvisioned$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        PaymentLinkLandingComponentPaymentLinkActions.exchangeTokenForSessionSuccess,
      ),
      concatLatestFrom(() => [
        this.store.select(selectGetSessionFromPaymentLinkResponse),
        this.store.select(selectVerifyUserIsProvisionedRetryConfig),
      ]),
      exhaustMap(([, session, retryConfig]) =>
        this.permissionService
          .v1TenantsTenantIdApplicationApplicationIdFeatureFeatureIdPersonaPersonaIdRoleProfileGet(
            session.tenantId,
            'payments-app',
            'arc-feature-paymentlinks',
            'btpay-paymentlinks-payor',
            session.accessToken,
          )
          .pipe(
            retry(retryConfig),
            map(() =>
              PaymentLinkLandingComponentPermissionActions.verifyUserIsProvisionedSuccess(),
            ),
            catchError((error: HttpErrorResponse) =>
              of(
                PaymentLinkLandingComponentPermissionActions.verifyUserIsProvisionedError(
                  {
                    error,
                  },
                ),
              ),
            ),
          ),
      ),
    ),
  );

  // Also re-fires on userProfileLanguageChanged: fetching the freshly
  // created guest profile after token exchange updates AuthUserProfileStore,
  // which independently re-triggers TranslateEffects.setUserLanguage$ and
  // clobbers the payment link's locale back to English. Re-asserting here
  // wins that race regardless of how many times the profile fetch resolves.
  restoreLanguageOnSessionLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        PaymentLinkLandingComponentPaymentLinkActions.exchangeTokenForSessionSuccess,
        TranslateActions.userProfileLanguageChanged,
      ),
      concatLatestFrom(() =>
        this.store.select(selectGetSessionFromPaymentLinkResponse),
      ),
      map(([, session]) => session?.locale?.toLowerCase()),
      filter((language): language is string => !!language),
      map((language) =>
        TranslateActions.redirectStateLanguageRestored({
          language: supportedLanguages.includes(language) ? language : 'en',
        }),
      ),
    ),
  );

  navigateOnTokenExchangeError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          PaymentLinkLandingComponentPaymentLinkActions.exchangeTokenForSessionError,
        ),
        exhaustMap((action) => {
          const errorCode = action.error.error?.Errors?.[0]?.Code;
          if (errorCode === 'PAYABLES_NOT_FOUND') {
            this.router.navigate(['/paymentlinks/error'], {
              state: { type: 'payables-closed' },
            });
          } else if (action.error.status === 404) {
            this.router.navigate(['/paymentlinks/expired']);
          } else {
            this.router.navigate(['/paymentlinks/error']);
          }
          return EMPTY;
        }),
      ),
    { dispatch: false },
  );
}
